import React, { useState } from "react";
import SearchableSelect from "~/common/SearchableSelect";
import { CompatibleModel } from "~/common/Store/models/Category";
import setWindowLocationHref from "~/common/navigation/setWindowLocationHref";
import {
  addParamToUrl,
  getParamValueFromUrl,
  removeParamFromUrl,
} from "./FilterUrlHelpers";

interface Props {
  pageUrl: string;
  compatibleModels: CompatibleModel[];
  isFilterRemoved?: boolean;
}

const PartsFinder = ({ pageUrl, compatibleModels, isFilterRemoved }: Props) => {
  const defaultManufacturer = getParamValueFromUrl(pageUrl, "manufacturer");
  const modelOrEquipmentItemNumber = getParamValueFromUrl(pageUrl, "model");
  let defaultModel = compatibleModels
    .find((x) => x.manufacturer === defaultManufacturer)
    ?.models.find(
      (x) => x.equipmentItemNumber === modelOrEquipmentItemNumber,
    )?.name;

  if (!defaultModel) {
    defaultModel = compatibleModels
      .find((x) => x.manufacturer === defaultManufacturer)
      ?.models.find((x) => x.name === modelOrEquipmentItemNumber)?.name;
  }

  const [manufacturer, setManufacturer] = useState(defaultManufacturer);

  const handleManufacturerChange = (input: string) => {
    setManufacturer(input);
  };

  const handleManufacturerClear = (): void => {
    if (defaultManufacturer) {
      let newUrl = removeParamFromUrl(pageUrl, "manufacturer");
      newUrl = removeParamFromUrl(newUrl, "model");
      setWindowLocationHref(newUrl);
    }
  };

  const handleModelChange = (input: string) => {
    if (input) {
      const selectedModelMatches = compatibleModels
        .find((x) => x.manufacturer === manufacturer)
        ?.models.find((x) => x.name === input);

      if (!selectedModelMatches) {
        return;
      }

      let newUrl = addParamToUrl(pageUrl, "manufacturer", manufacturer);
      newUrl = addParamToUrl(
        newUrl,
        "model",
        selectedModelMatches.equipmentItemNumber || input,
      );
      setWindowLocationHref(newUrl);
    }
  };

  const models = compatibleModels
    .find((x) => x.manufacturer === manufacturer)
    ?.models.map((x) => x.name);

  const hasManufacturerSelected = compatibleModels.find(
    (x) => x.manufacturer === manufacturer,
  );

  return (
    <li className="filter-list__section filter-list__section--parts-filter py-2 px-2 bg-gray-100">
      <div className="flex flex-col flex-no-wrap justify-start relative mb-2">
        <span className="absolute left-0 top-2">
          <svg
            width="18"
            height="18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.978 9.483v-.988a.726.726 0 00-.583-.712l-1.811-.362a5.773 5.773 0 00-.53-1.278l1.024-1.537a.726.726 0 00-.09-.917l-.699-.698a.726.726 0 00-.916-.091l-1.537 1.024a5.777 5.777 0 00-1.278-.53l-.363-1.81A.726.726 0 009.483 1h-.988a.726.726 0 00-.712.584l-.362 1.81a5.77 5.77 0 00-1.278.53L4.606 2.9a.726.726 0 00-.917.09l-.698.7a.726.726 0 00-.091.916l1.025 1.537c-.225.399-.404.827-.53 1.278l-1.811.362A.726.726 0 001 8.495v.988c0 .346.244.644.584.712l1.81.363c.127.45.305.879.53 1.278L2.9 13.373a.726.726 0 00.09.916l.7.699a.726.726 0 00.916.09l1.536-1.024c.4.225.828.403 1.279.53l.362 1.81c.068.34.366.584.712.584h.988a.726.726 0 00.712-.584l.362-1.81c.451-.127.88-.305 1.279-.53l1.537 1.024a.726.726 0 00.916-.09l.699-.699a.726.726 0 00.09-.916l-1.024-1.537c.225-.4.403-.828.53-1.278l1.81-.363a.726.726 0 00.584-.712zm-7.989 1.25a1.744 1.744 0 100-3.488 1.744 1.744 0 000 3.488z"
              fill="#3B3D3B"
            />
            <path
              d="M16.395 7.783l.073-.368-.073.368zm-1.811-.362l-.361.1.061.222.226.045.074-.367zm-.53-1.278l-.312-.208-.128.191.113.2.327-.183zm1.024-1.537l.313.208-.313-.208zm-.09-.917l-.265.265.265-.265zm-.699-.698l-.265.265.265-.265zm-.916-.091l.208.312-.208-.312zm-1.537 1.024l-.184.327.2.113.192-.128-.208-.312zm-1.278-.53l-.368.074.045.226.222.062.1-.361zm-.363-1.81l-.367.073.367-.073zm-2.412 0l.368.073-.368-.073zm-.362 1.81l.1.362.222-.062.046-.226-.368-.073zm-1.278.53l-.208.312.191.128.2-.113-.183-.327zM4.606 2.9l.208-.312-.208.312zm-.917.09l-.265-.265.265.266zm-.698.7l.265.264-.265-.265zm-.091.916l.312-.208-.312.208zm1.025 1.537l.326.184.113-.201-.127-.192-.313.208zm-.53 1.278l.073.367.226-.045.062-.221-.361-.101zm-1.811.362l.073.368-.073-.368zm0 2.412l-.074.368.074-.368zm1.81.363l.362-.101-.062-.222-.226-.045-.073.368zm.53 1.278l.312.208.128-.192-.113-.2-.327.184zM2.9 13.373l-.312-.208.312.208zm.09.916l-.265.265.265-.265zm.7.699l.264-.265-.265.265zm.916.09l-.208-.312.208.313zm1.536-1.024l.184-.327-.2-.113-.192.128.208.312zm1.279.53l.367-.074-.045-.226-.221-.062-.101.362zm.362 1.81l.368-.073-.368.073zm2.412 0l-.367-.073.367.073zm.362-1.81l-.1-.362-.222.062-.045.226.367.074zm1.279-.53l.208-.312-.192-.128-.2.113.184.327zm1.537 1.024l-.209.313.209-.313zm1.615-.789l-.266-.265.266.265zm.09-.916l-.312.208.312-.208zm-1.024-1.537l-.327-.184-.113.2.128.192.312-.208zm.53-1.278l-.074-.368-.226.045-.062.222.361.1zm1.81-.363l-.073-.367.073.367zm.21-1.7v.988h.75v-.988h-.75zm-.283-.344a.351.351 0 01.282.344h.75c0-.525-.37-.977-.885-1.08l-.147.736zm-1.81-.363l1.81.363.147-.736-1.81-.362-.148.735zm-.784-1.461a5.4 5.4 0 01.495 1.195l.723-.202a6.151 6.151 0 00-.564-1.362l-.654.369zm1.04-1.93l-1.025 1.538.624.416 1.024-1.537-.624-.416zm-.044-.443a.351.351 0 01.043.444l.624.416a1.101 1.101 0 00-.137-1.39l-.53.53zm-.7-.698l.7.698.53-.53-.699-.699-.53.53zm-.442-.044a.351.351 0 01.443.044l.53-.53a1.101 1.101 0 00-1.39-.138l.417.624zm-1.537 1.024l1.537-1.024-.416-.624-1.537 1.024.416.624zm-1.588-.48c.422.118.822.285 1.196.495l.368-.653a6.152 6.152 0 00-1.361-.564l-.203.722zm-.628-2.099l.362 1.811.735-.147-.362-1.81-.735.146zm-.345-.282c.168 0 .312.118.345.282l.735-.147a1.101 1.101 0 00-1.08-.885v.75zm-.988 0h.988v-.75h-.988v.75zm-.344.282a.351.351 0 01.344-.282v-.75c-.525 0-.977.37-1.08.885l.736.147zm-.362 1.811l.362-1.81-.736-.148-.362 1.811.736.147zm-1.462.783c.373-.21.773-.377 1.195-.495l-.202-.722c-.48.134-.936.324-1.361.564l.368.653zm-1.93-1.04l1.538 1.025.416-.624-1.537-1.024-.416.624zm-.442.045a.351.351 0 01.443-.044l.416-.624a1.101 1.101 0 00-1.39.137l.53.53zm-.7.698l.7-.698-.53-.53-.7.698.53.53zm-.043.444a.351.351 0 01.044-.444l-.53-.53a1.101 1.101 0 00-.138 1.39l.624-.416zm1.025 1.536L3.212 4.398l-.624.416L3.612 6.35l.625-.417zm-.481 1.588c.118-.422.285-.822.495-1.195l-.653-.369c-.24.426-.43.882-.564 1.362l.722.202zm-2.099.629l1.811-.363-.147-.735-1.81.362.146.736zm-.282.344c0-.167.118-.312.282-.344l-.147-.736a1.101 1.101 0 00-.885 1.08h.75zm0 .988v-.988h-.75v.988h.75zm.282.345a.351.351 0 01-.282-.345h-.75c0 .525.37.977.885 1.08l.147-.735zm1.811.362l-1.81-.362-.148.735 1.811.362.147-.735zm.783 1.462a5.4 5.4 0 01-.495-1.195l-.723.202c.135.48.325.936.565 1.36l.653-.367zm-1.04 1.929l1.025-1.537-.624-.416-1.024 1.537.624.416zm.045.443a.351.351 0 01-.044-.443l-.624-.416a1.101 1.101 0 00.137 1.39l.53-.531zm.698.699l-.698-.7-.53.531.698.699.53-.53zm.444.043a.351.351 0 01-.444-.043l-.53.53c.371.371.953.429 1.39.137l-.416-.623zm1.536-1.024l-1.536 1.024.416.624 1.536-1.024-.416-.624zm1.588.48a5.4 5.4 0 01-1.196-.495l-.368.654c.426.24.882.43 1.362.564l.202-.723zm.629 2.099l-.363-1.811-.735.147.362 1.811.736-.147zm.344.282a.351.351 0 01-.344-.282l-.736.147c.103.515.555.885 1.08.885v-.75zm.988 0h-.988v.75h.988v-.75zm.345-.282a.351.351 0 01-.345.282v.75c.525 0 .977-.37 1.08-.885l-.736-.147zm.362-1.811l-.362 1.81.735.148.362-1.81-.735-.148zm1.462-.783c-.374.21-.774.377-1.196.495l.202.723c.48-.135.937-.325 1.362-.564l-.368-.654zm1.929 1.04l-1.537-1.025-.416.624 1.536 1.024.417-.624zm.443-.044a.351.351 0 01-.444.043l-.415.624a1.101 1.101 0 001.389-.137l-.53-.53zm.698-.7l-.698.7.53.53.699-.699-.53-.53zm.044-.442a.351.351 0 01-.044.443l.53.53a1.1 1.1 0 00.138-1.39l-.624.417zm-1.024-1.537l1.024 1.537.624-.416-1.024-1.537-.624.416zm.48-1.588a5.394 5.394 0 01-.495 1.196l.653.368c.24-.425.43-.882.565-1.361l-.723-.203zm2.099-.628l-1.811.362.147.735 1.811-.362-.147-.735zm.282-.345a.351.351 0 01-.282.345l.147.735c.515-.103.885-.555.885-1.08h-.75zm-6.245-.494c0 .756-.613 1.369-1.369 1.369v.75c1.17 0 2.119-.949 2.119-2.119h-.75zM8.989 7.62c.756 0 1.369.613 1.369 1.37h.75c0-1.171-.949-2.12-2.119-2.12v.75zM7.62 8.99c0-.757.613-1.37 1.37-1.37v-.75c-1.171 0-2.12.949-2.12 2.12h.75zm1.37 1.368a1.369 1.369 0 01-1.37-1.369h-.75c0 1.17.949 2.119 2.12 2.119v-.75z"
              fill="#3B3D3B"
            />
          </svg>
        </span>
        <span className="ml-6 text-sm font-bold w-full">Parts Filter</span>
        <span className="ml-6 text-xs">Filter By Manufacturer &amp; Model</span>
      </div>
      <SearchableSelect
        placeholder="Manufacturer"
        options={compatibleModels.map((x) => x.manufacturer)}
        label="Select Manufacturer"
        handleChange={handleManufacturerChange}
        defaultOption={defaultManufacturer}
        handleClear={handleManufacturerClear}
        disabled={!!defaultManufacturer}
      />
      <SearchableSelect
        placeholder="Model"
        options={models || []}
        label="Select Model"
        disabled={!hasManufacturerSelected}
        handleChange={handleModelChange}
        defaultOption={defaultModel}
        isFilterRemoved={isFilterRemoved}
      />
    </li>
  );
};

export default PartsFinder;
